/**
 * @fileoverview A theme is VALUES for named tokens. It is never CSS, and this
 * file is where that stops being a slogan.
 *
 * One addressable theme event can be referenced by many pages, so editing the
 * theme restyles all of them at once. That reuse is the whole feature: it is
 * what makes this automated formatting rather than a stylesheet with extra
 * steps.
 *
 * THE FAILURE MODE THE DESIGN NAMES, and the reason for every restriction
 * below: a theme that grows into a general styling language is CSS again with
 * worse ergonomics and no safety argument left. A page that genuinely needs
 * more than these tokens express is what the raw HTML escape hatch is for. If
 * something here ever wants an expressive mechanism -- a selector, a rule, a
 * nested value, a way to say "and also" -- that is the moment to stop, because
 * the boundary is the product decision and not an implementation detail.
 *
 * THREE THINGS KEEP IT BOUNDED. Each is a separate wall and none is sufficient
 * alone.
 *
 * 1. THE TOKEN NAMES ARE AN ALLOW-LIST, and it is exactly the set of tokens the
 *    page stylesheet actually reads inside the page's own subtree. Not "every
 *    token the fleet defines", which would let a theme set values that silently
 *    do nothing, and not a list we invented, which would drift away from what
 *    the stylesheet reads. A theme can change what a page looks like and cannot
 *    reach anything else.
 *
 * 2. EVERY VALUE MUST MATCH A GRAMMAR, and the grammars are allow-lists too.
 *    This is the wall that carries the actual risk, and the risk is not script.
 *    It is `url(...)`. A theme that can put a URL in any value makes EVERY
 *    READER'S BROWSER FETCH IT, which hands the theme's author the IP address
 *    of everyone who reads any page using that theme. That is surveillance
 *    arriving dressed as a font, and it is precisely what this product exists
 *    to refuse. A blocklist would be the wrong shape: CSS escapes mean `\75
 *    rl(` is also `url(`, so any list of forbidden spellings is a list somebody
 *    gets around. A value is accepted only if it matches a colour, a length, a
 *    number or a font stack in full, and anything else is dropped.
 *
 * 3. THE TOKENS ARE APPLIED TO THE PAGE'S SUBTREE, NEVER TO `:root`. A theme
 *    that could reach the document root would be restyling OUR chrome -- the
 *    header, the app switcher, the sign-in control -- from a stranger's event.
 *    "Formatting is data" has to stop at the edge of the page, or it becomes
 *    "the page controls our interface" and a hostile theme can make a Cloistr
 *    control look like something it is not. See `themeStyle`.
 *
 * WHO IS TRUSTING WHOM, said plainly because it is not obvious. The theme is
 * fetched when the page is READ, so an author who references somebody else's
 * theme is trusting that person CONTINUOUSLY rather than once. The theme author
 * can change it afterwards and every page pointing at it changes with it. That
 * is the reuse working as designed rather than a hole, but an author should be
 * choosing it knowingly, and a bare address in a tag does not make it visible.
 *
 * A HOSTILE THEME CAN STILL MAKE A PAGE UGLY OR UNREADABLE -- text the colour
 * of its own background, a measure of two characters. That is deliberately not
 * defended against. It harms only the look of a page whose author chose that
 * theme, it is undone by changing one tag, and defending it would mean
 * second-guessing legitimate design choices with a heuristic. The magnitudes
 * below are clamped against the ACCIDENT (a spacing of ten thousand rem makes a
 * page look like our app is broken rather than like a bad theme, so the reader
 * blames the wrong thing) and not against an author styling their own page.
 *
 * SHAPE OF A 33302, ours, because nothing specified it:
 *
 *   d        the theme's stable identifier            REQUIRED
 *   title    human name for the theme
 *   token    ['token', '<name>', '<value>']; REPEATABLE
 *   .content a plaintext description, for NIP-50 search
 *
 * Tags rather than a JSON blob in `.content`, for the same reason the page
 * index uses tags: a client that does not know what a `token` tag is ignores it
 * and loses nothing, whereas a client handed JSON in a field it expects to be
 * prose renders the JSON at a reader.
 *
 * FLAGGED TO `cloistr-orchestrator`: nothing in the design says what a theme
 * event looks like, so the shape above is a choice made here. Easy to change
 * while there is one publisher and no theme in the wild.
 */

/**
 * What kind of value a token accepts. Five, and adding a sixth is a decision.
 * The fifth, `measure`, was that decision: confirmed by cloistr-orchestrator
 * 2026-10-15 so a theme can say "60% of the window, capped".
 */
type TokenGrammar = 'colour' | 'length' | 'number' | 'fontStack' | 'measure';

/**
 * The vocabulary: every token a theme may set, and nothing else.
 *
 * DERIVED FROM THE STYLESHEET RATHER THAN CHOSEN. This is exactly the set of
 * custom properties `page.css` reads inside the page subtree, which is what
 * makes the bound honest: every name here changes something a reader can see,
 * and a name absent from here would have been a value that did nothing.
 *
 * The public name drops the `--cloistr-` / `--pages-` prefix. Which of our two
 * prefixes a token happens to carry is an implementation detail of our own
 * stylesheet and has no business inside portable content someone else may
 * render.
 */
const VOCABULARY: Record<string, { property: string; grammar: TokenGrammar }> = {
  // Colours.
  text: { property: '--cloistr-text', grammar: 'colour' },
  'text-muted': { property: '--cloistr-text-muted', grammar: 'colour' },
  'text-dim': { property: '--cloistr-text-dim', grammar: 'colour' },
  border: { property: '--cloistr-border', grammar: 'colour' },
  'bg-elevated': { property: '--cloistr-bg-elevated', grammar: 'colour' },
  primary: { property: '--cloistr-primary', grammar: 'colour' },
  'primary-hover': { property: '--cloistr-primary-hover', grammar: 'colour' },
  // The page's own surface. Transparent by default, so an unthemed page sits
  // on the app background exactly as before.
  surface: { property: '--pages-surface', grammar: 'colour' },
  // One per callout severity, the fixed set the format spec names.
  'callout-note': { property: '--pages-callout-note', grammar: 'colour' },
  'callout-tip': { property: '--pages-callout-tip', grammar: 'colour' },
  'callout-warning': { property: '--pages-callout-warning', grammar: 'colour' },
  'callout-caution': { property: '--pages-callout-caution', grammar: 'colour' },
  'callout-important': { property: '--pages-callout-important', grammar: 'colour' },
  // Code blocks: a surface, the text, and five syntax roles. Highlighting
  // emits classes; these are the only colours it ever shows.
  'code-bg': { property: '--pages-code-bg', grammar: 'colour' },
  'code-text': { property: '--pages-code-text', grammar: 'colour' },
  'code-keyword': { property: '--pages-code-keyword', grammar: 'colour' },
  'code-string': { property: '--pages-code-string', grammar: 'colour' },
  'code-comment': { property: '--pages-code-comment', grammar: 'colour' },
  'code-number': { property: '--pages-code-number', grammar: 'colour' },
  'code-title': { property: '--pages-code-title', grammar: 'colour' },

  // Fonts.
  'font-sans': { property: '--cloistr-font-sans', grammar: 'fontStack' },
  'font-mono': { property: '--cloistr-font-mono', grammar: 'fontStack' },

  // Spacing.
  'space-xs': { property: '--cloistr-space-xs', grammar: 'length' },
  'space-sm': { property: '--cloistr-space-sm', grammar: 'length' },
  'space-md': { property: '--cloistr-space-md', grammar: 'length' },
  'space-lg': { property: '--cloistr-space-lg', grammar: 'length' },
  'space-xl': { property: '--cloistr-space-xl', grammar: 'length' },

  // Corners.
  'radius-sm': { property: '--cloistr-radius-sm', grammar: 'length' },
  'radius-md': { property: '--cloistr-radius-md', grammar: 'length' },

  // Reading measure.
  measure: { property: '--pages-measure', grammar: 'measure' },
  'measure-narrow': { property: '--pages-measure-narrow', grammar: 'measure' },
  'measure-wide': { property: '--pages-measure-wide', grammar: 'measure' },

  // Type scale.
  'text-base': { property: '--pages-text-base', grammar: 'length' },
  'text-lead': { property: '--pages-text-lead', grammar: 'length' },
  'line-height': { property: '--pages-line-height', grammar: 'number' },
};

/**
 * EVERY COLOUR CARRIES A DARK VALUE, as `<name>.dark`. The format spec settled
 * per-scheme colours with the operator on 2026-09-16, specifically so the dark
 * half is not a retrofit that makes every theme in the wild wrong. The plain
 * name is the light value, which is what every theme written before this
 * already means.
 *
 * A dark sibling is a separate token with the SAME property, so the existing
 * allow-list and grammar cover it unchanged; `themeStyle` picks one or the
 * other by the reader's scheme.
 */
const DARK_SUFFIX = '.dark';
for (const [name, token] of Object.entries(VOCABULARY)) {
  if (token.grammar === 'colour') VOCABULARY[`${name}${DARK_SUFFIX}`] = token;
}

/** The token names a theme may use, for an authoring UI to offer. */
export const THEME_TOKEN_NAMES: readonly string[] = Object.keys(VOCABULARY);

/** What a given token accepts, for an authoring UI to explain. */
export function grammarOf(name: string): TokenGrammar | null {
  return VOCABULARY[name]?.grammar ?? null;
}

/** Which colour scheme the reader is in. */
export type ColourScheme = 'light' | 'dark';

/**
 * A theme an author is writing, before it is an event.
 *
 * `tokens` is keyed by the PUBLIC token name -- the unprefixed one in
 * `VOCABULARY` -- and its values are exactly what the author typed. Nothing
 * here is validated: validation belongs to `parseThemeEvent`, and a draft that
 * pre-validated would be a second grammar. See `publishTheme`.
 */
export interface ThemeDraft {
  identifier: string;
  title: string;
  /** Plaintext, for NIP-50 relay search. Becomes `.content`. */
  description: string;
  tokens: Record<string, string>;
  /**
   * Tags this draft's editor has no control for, carried through untouched.
   *
   * THE SHIPPED 33302 SHAPE IS FULLY COVERED BY THE CONTROLS -- `d`, `title`,
   * `token` and the description -- so today this is always empty, and it is
   * here anyway. The page editor learned the same lesson the expensive way:
   * republishing only the fields that have inputs is data loss wearing the
   * clothes of a save, and the moment a tag is added to this shape without a
   * control beside it, the editor that strips it will already be shipped.
   *
   * It does NOT rescue a token this reader rejects. A rejected token is a value
   * the author must fix, which is why `publishTheme` refuses rather than
   * carries it.
   */
  carried?: string[][];
}

/**
 * A draft as tags, ready to become an event.
 *
 * IT DOES NOT FILTER BY THE VOCABULARY, and that is the important line in this
 * function. A builder that quietly dropped a token name it did not recognise
 * would hand the publisher a clean event, the publisher would report success,
 * and the author's misspelled token would have vanished between the keyboard
 * and the relay with nobody saying so. Every name the author supplied is
 * written, so the reader's parse is what refuses it and can name it.
 *
 * AN EMPTY VALUE IS DIFFERENT AND IS SKIPPED. A name with nothing against it is
 * a token the author did not set, which is an absence rather than a rejected
 * setting, and dropping an absence loses nothing. This is the one exception and
 * the distinction is the whole of it: an unknown NAME is a mistake to report, a
 * missing VALUE is a field left blank.
 *
 * Tokens are emitted in vocabulary order so that saving an unchanged theme
 * produces an identical event, rather than one that differs by tag order and
 * looks like a change to anything comparing versions.
 */
export function buildThemeTags(draft: ThemeDraft): { tags: string[][]; content: string } {
  const tags: string[][] = [['d', draft.identifier]];

  const title = draft.title.trim();
  if (title) tags.push(['title', title]);

  const supplied = Object.keys(draft.tokens);
  const ordered = [
    ...THEME_TOKEN_NAMES.filter((name) => name in draft.tokens),
    ...supplied.filter((name) => !(name in VOCABULARY)),
  ];

  for (const name of ordered) {
    const value = draft.tokens[name];
    if (value === undefined || value.trim() === '') continue;
    tags.push(['token', name, value.trim()]);
  }

  // Anything the editor did not understand, after the tags it did. Filtered
  // against the three names built above so a carried tag cannot become a second
  // `d` and quietly move the theme to another address.
  for (const tag of draft.carried ?? []) {
    if (tag[0] === 'd' || tag[0] === 'title' || tag[0] === 'token') continue;
    tags.push(tag);
  }

  return { tags, content: draft.description.trim() };
}

export interface ThemeAddress {
  kind: number;
  pubkey: string;
  identifier: string;
}

export interface PageTheme {
  address: ThemeAddress;
  title: string;
  /**
   * Validated values, keyed by the CSS custom property each one sets. Colours
   * here are the LIGHT values; see `darkVariables`.
   */
  variables: Record<string, string>;
  /** Validated `.dark` colour values, keyed the same way. */
  darkVariables: Record<string, string>;
  /**
   * Token names the event carried that this renderer did not accept, because
   * the name is outside the vocabulary or the value failed its grammar.
   *
   * KEPT RATHER THAN DISCARDED so an authoring UI can tell an author what was
   * dropped. A theme whose values silently do nothing is the same class of
   * defect as a publish that silently succeeds, and this repo has been bitten
   * by that twice already. Deliberately NOT surfaced on the reading path: a
   * reader has no use for it and no ability to act on it.
   */
  rejected: string[];
  createdAt: number;
}

/** The minimum of a Nostr event this module needs. */
export interface RawThemeEvent {
  kind: number;
  pubkey: string;
  created_at: number;
  content: string;
  tags: string[][];
}

const HEX_64 = /^[0-9a-f]{64}$/;

/**
 * Parse a theme event, dropping anything that does not belong.
 *
 * Returns null only when the event is not a theme at all. A theme whose tokens
 * are ALL rejected still parses, as a theme that changes nothing: the page
 * renders in default tokens, which is the same outcome as no theme and is the
 * graceful-degradation contract seen from our own side rather than someone
 * else's.
 */
export function parseThemeEvent(event: RawThemeEvent, kind: number): PageTheme | null {
  if (event.kind !== kind) return null;
  if (!HEX_64.test(event.pubkey)) return null;

  const identifier = event.tags.find((t) => t[0] === 'd' && t.length > 1)?.[1];
  if (identifier === undefined) return null;

  const variables: Record<string, string> = {};
  const darkVariables: Record<string, string> = {};
  const rejected: string[] = [];

  for (const tag of event.tags) {
    if (tag[0] !== 'token') continue;
    const name = tag[1];
    const value = tag[2];
    if (typeof name !== 'string' || typeof value !== 'string') continue;

    const token = VOCABULARY[name];
    if (!token) {
      rejected.push(name);
      continue;
    }

    const accepted = validate(value, token.grammar);
    if (accepted === null) {
      rejected.push(name);
      continue;
    }

    // First wins. A repeated token is a malformed theme rather than an
    // override, and taking the last would make the meaning depend on tag order,
    // which nothing in the protocol promises is preserved.
    const into = name.endsWith(DARK_SUFFIX) ? darkVariables : variables;
    if (!(token.property in into)) into[token.property] = accepted;
  }

  return {
    address: { kind: event.kind, pubkey: event.pubkey, identifier },
    title: event.tags.find((t) => t[0] === 'title' && t.length > 1)?.[1] ?? identifier,
    variables,
    darkVariables,
    rejected,
    createdAt: event.created_at,
  };
}

/**
 * A value, or null if it is not one this grammar accepts.
 *
 * ALLOW-LIST, NOT SANITISE. Nothing here strips a dangerous substring out of a
 * value and keeps the remainder: a value matches in full or it is dropped in
 * full. Sanitising would mean reasoning about what a partially rewritten CSS
 * value means, and that answer differs per browser.
 */
function validate(raw: string, grammar: TokenGrammar): string | null {
  const value = raw.trim();
  // A bound before any pattern runs. None of these grammars needs length, and
  // an unbounded input is what turns a regular expression into a denial of
  // service.
  if (value.length === 0 || value.length > 120) return null;

  switch (grammar) {
    case 'colour':
      return validateColour(value);
    case 'length':
      return validateLength(value);
    case 'number':
      return validateNumber(value);
    case 'fontStack':
      return validateFontStack(value);
    case 'measure':
      return validateMeasure(value);
  }
}

const HEX_COLOUR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const COLOUR_FUNCTION = /^(rgb|rgba|hsl|hsla)\(([^()]*)\)$/i;
const COLOUR_ARGUMENT = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:%|deg|grad|rad|turn)?$/i;

/**
 * A small, explicit set of names.
 *
 * Bounded on purpose: the full CSS colour keyword list is 148 names, and
 * carrying a dictionary to save an author typing six hex digits is the kind of
 * growth this file exists to refuse.
 */
const COLOUR_KEYWORDS = new Set([
  'black',
  'white',
  'gray',
  'grey',
  'silver',
  'red',
  'green',
  'blue',
  'yellow',
  'orange',
  'purple',
  'brown',
  'pink',
  'teal',
  'navy',
  'transparent',
  'currentcolor',
  'inherit',
]);

function validateColour(value: string): string | null {
  if (HEX_COLOUR.test(value)) return value;
  if (COLOUR_KEYWORDS.has(value.toLowerCase())) return value;

  const fn = COLOUR_FUNCTION.exec(value);
  if (!fn) return null;
  // The `[^()]*` above already guarantees no nesting, so no function call can
  // hide inside the arguments. Each argument still has to be a bare number.
  const args = (fn[2] ?? '').split(/[\s,/]+/).filter(Boolean);
  if (args.length < 3 || args.length > 4) return null;
  return args.every((a) => COLOUR_ARGUMENT.test(a)) ? value : null;
}

const LENGTH = /^(\d+\.?\d*|\.\d+)(px|rem|em|ch|ex|vw|vh|vmin|vmax|%)$/;

/**
 * Non-negative only, and clamped.
 *
 * Every length token here is a spacing, a radius, a measure or a font size, and
 * none of those means anything negative. Rejecting the sign is narrower than
 * allowing it and costs an author nothing they would have wanted.
 *
 * The magnitude bound is against the ACCIDENT rather than the attack. A spacing
 * of ten thousand rem compromises nothing; it produces a page that looks like
 * our app is broken rather than like a bad theme, so the reader blames us.
 */
function validateLength(value: string): string | null {
  if (value === '0') return '0';
  const match = LENGTH.exec(value);
  if (!match) return null;
  const magnitude = Number(match[1]);
  if (!Number.isFinite(magnitude) || magnitude > 1000) return null;
  return value;
}

const MEASURE_MIN = /^min\(\s*([^,()\s]+)\s*,\s*([^,()\s]+)\s*\)$/;

/**
 * A reading measure: a length, or `min(<length>, <length>)` and nothing more.
 *
 * ONE FUNCTION, TWO ARGUMENTS, NO NESTING. Enough to say "60% of the window,
 * but never more than 150 characters", which is what the fleet theme needed
 * and a plain length cannot express. Each argument must pass the length
 * grammar on its own, so nothing can hide inside the function that the length
 * wall would have refused. Not `calc`, `max` or `clamp`: each is a step toward
 * arithmetic in a value, which is CSS again.
 */
function validateMeasure(value: string): string | null {
  const m = MEASURE_MIN.exec(value);
  if (!m) return validateLength(value);
  const a = validateLength(m[1]!);
  const b = validateLength(m[2]!);
  return a !== null && b !== null ? `min(${a}, ${b})` : null;
}

/** Line height. Unitless, and within the range a line of text is readable in. */
function validateNumber(value: string): string | null {
  if (!/^\d+\.?\d*$/.test(value)) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0.8 && n <= 4 ? value : null;
}

const FONT_QUOTED = /^(["'])([A-Za-z0-9 _-]{1,40})\1$/;
const FONT_BARE = /^[A-Za-z][A-Za-z0-9-]{0,39}$/;

/**
 * A font stack: names, commas, nothing else.
 *
 * THIS IS THE TOKEN MOST WORTH BEING STRICT ABOUT, because a font is the one
 * place a reader would not think twice about a value that fetches something. A
 * family name is a NAME. It never contains a URL, and a theme cannot ship a
 * font file: a web font is a network request on every reader's machine, to a
 * host chosen by whoever wrote the theme, which is the leak described at the
 * top of this file. An author who wants a particular font names it, and readers
 * who have it see it.
 */
function validateFontStack(value: string): string | null {
  const families = value.split(',').map((f) => f.trim());
  if (families.length === 0 || families.length > 6) return null;
  const ok = families.every((f) => FONT_QUOTED.test(f) || FONT_BARE.test(f));
  return ok ? families.join(', ') : null;
}

/**
 * The theme as inline custom properties, for the element that wraps the page.
 *
 * SCOPED, AND THAT IS THE POINT. These land on the page's own element, so they
 * are visible to the page's own declarations and to nothing above it. Setting
 * them on `:root` instead would hand a stranger's event control of our header
 * and every other Cloistr surface on the screen. See the third wall at the top
 * of this file.
 *
 * Returned as a plain object rather than written to the DOM here, so the
 * component owns when it applies and React owns the element.
 */
export function themeStyle(theme: PageTheme | null, scheme: ColourScheme = 'light'): Record<string, string> {
  if (!theme) return {};
  if (scheme === 'light') return withPrimaryText({ ...theme.variables });
  // DARK: every value that is not a colour applies as is, and a colour applies
  // only if the theme gave a dark value for it. A light colour carried into
  // dark is how dark text lands on a dark background, so a theme that says
  // nothing about dark leaves the app's dark colours in place.
  const out: Record<string, string> = {};
  for (const [property, value] of Object.entries(theme.variables)) {
    if (!COLOUR_PROPERTIES.has(property)) out[property] = value;
  }
  return withPrimaryText({ ...out, ...theme.darkVariables });
}

/**
 * A theme's `primary` is also its link TEXT colour.
 *
 * @cloistr/ui split the app's primary into a fill (`--cloistr-primary`) and a
 * darker text shade (`--cloistr-primary-text`), and the stylesheet colours
 * links from the text one. A theme has one `primary`, and it is what the
 * author chose for their links: without this, every themed page shows the
 * app's own link colour, which was never checked against the theme's sheet.
 * Not a token of its own, because a second name for the same choice is one
 * more thing for a theme author to keep in step.
 */
function withPrimaryText(style: Record<string, string>): Record<string, string> {
  const primary = style['--cloistr-primary'];
  return primary === undefined ? style : { ...style, '--cloistr-primary-text': primary };
}

const COLOUR_PROPERTIES = new Set(
  Object.values(VOCABULARY)
    .filter((t) => t.grammar === 'colour')
    .map((t) => t.property)
);

/**
 * Parse a `<kind>:<pubkey>:<identifier>` coordinate, or null.
 *
 * The page's `theme` tag carries this verbatim, so it is validated here rather
 * than trusted: it is a string out of somebody else's event and it is about to
 * become a relay query.
 */
export function parseThemeAddress(coordinate: string): ThemeAddress | null {
  const parts = coordinate.split(':');
  if (parts.length !== 3) return null;

  const [kindText, pubkey, identifier] = parts;
  // The length check above already makes all three present. Saying so again is
  // what lets the compiler agree, and it costs a line.
  if (kindText === undefined || pubkey === undefined || identifier === undefined) return null;

  // Digits only, BEFORE `Number`, which happily reads `0x81`, ` 33302 `, `1e3`
  // and `Infinity`. Every one of those would name a real kind by a spelling
  // nothing else in the fleet would produce.
  if (!/^\d+$/.test(kindText)) return null;
  const kind = Number(kindText);
  if (!Number.isInteger(kind) || kind < 0 || kind > 65535) return null;
  if (!HEX_64.test(pubkey)) return null;

  return { kind, pubkey, identifier };
}
